import { Button, Card, CardBody, CardHeader, Text } from "@chakra-ui/react";
import { add } from "dinero.js";
import {
  FeeChargeEventsSelect,
  getFundFamilyOutstandingFeeChargeEvents,
} from "features/capital-call/FeeChargeEventSelect";
import { FeeSummaryTable } from "features/capital-call/FeeSummaryTable";
import { useAvailableFeeChargeEventsForCallRetrieveSuspense } from "gen/endpoints/fee-charge-event-select/fee-charge-event-select";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { OutstandingFeeChargeEventsByFundFunds } from "gen/model/outstandingFeeChargeEventsByFundFunds";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import {
  ChangeTable,
  FundChangeTableData,
} from "shared/components/change/ChangeTable";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { tryMoneyFromString, zeroMoney } from "shared/utils/MoneyUtils";

interface ManagementFeeHoldbackCardProps {
  setFeeChargeEventIds: React.Dispatch<React.SetStateAction<string[]>>;
  feeChargeEventIds: string[];
  tableRows: FundFamilyUseAllocationTableRow[] | undefined;
  closeComponent: () => void;
  feeId: string;
  funds: FundEntityReadData[];
}

export const ManagementFeeHoldbackCard: React.FC<
  ManagementFeeHoldbackCardProps
> = ({ closeComponent, ...props }) => {
  const { t } = useTranslation(["distribution", "common", "capitalCall"]);
  return (
    <Card variant="table">
      <CardHeader>
        <Text>{t("distribution:feeHoldback.title")}</Text>
        <Button size="xs" colorScheme="secondary" onClick={closeComponent}>
          {t("common:remove")}
        </Button>
      </CardHeader>
      <CardBody>
        <QueryBoundary>
          <ManagementFeeHoldbackCardBody {...props} />
        </QueryBoundary>
      </CardBody>
    </Card>
  );
};

const ManagementFeeHoldbackCardBody: React.FC<
  Omit<ManagementFeeHoldbackCardProps, "closeComponent">
> = ({ feeId, funds, tableRows, feeChargeEventIds, setFeeChargeEventIds }) => {
  const { fundFamilyId } = useRequiredStringParams();
  const { t } = useTranslation(["distribution", "common", "capitalCall"]);

  const { data: availableEventData } =
    useAvailableFeeChargeEventsForCallRetrieveSuspense(fundFamilyId, {
      fee_id: feeId,
    });
  const availableEvents =
    availableEventData.funds as Partial<OutstandingFeeChargeEventsByFundFunds>;
  const changeTableData = useMemo<FundChangeTableData>(
    () =>
      funds.reduce(
        (data, fund) => ({
          ...data,
          [fund.id]: {
            available: (availableEvents[fund.id] || []).reduce(
              (available, event) => {
                if (!feeChargeEventIds.includes(event.id)) {
                  return available;
                }
                return add(
                  available,
                  tryMoneyFromString(event.amount_outstanding_to_date),
                );
              },
              zeroMoney(),
            ),
            change: tryMoneyFromString(
              tableRows?.[0]?.funds?.[fund.id]?.[feeId]?.amount,
            ),
            fundName: fund.legal_name,
          },
        }),
        {},
      ),
    [funds, tableRows, availableEvents, feeChargeEventIds, feeId],
  );

  return (
    <>
      <Text textStyle="small" mb="16px">
        {t("distribution:feeHoldback.description")}
      </Text>
      <FeeChargeEventsSelect
        title={t("capitalCall:editor.selectTimePeriods")}
        value={feeChargeEventIds}
        onChangeValue={setFeeChargeEventIds}
        availableEvents={getFundFamilyOutstandingFeeChargeEvents(
          availableEventData,
        )}
        controlSx={{ maxWidth: "456px" }}
      />
      <QueryBoundary>
        {!!feeChargeEventIds.length && (
          <FeeSummaryTable
            feeChargeEventIds={feeChargeEventIds}
            isCollapsed={true}
            title={t("distribution:feeHoldback.historicalFeeBreakdown")}
            groupByFund={true}
          />
        )}
      </QueryBoundary>
      <ChangeTable
        title={t("distribution:feeHoldback.holdbackSummaryByFundEntity")}
        changeLabel={t("distribution:feeHoldback.heldback")}
        tableData={changeTableData}
        boxProps={{ mt: "12px" }}
      />
    </>
  );
};
